import { Tables, TablesInsert, TablesUpdate } from "@/types/database.types";
import { supabase } from "@/integrations/supabase/client";

// Type aliases for cleaner code
type AIAssistant = Tables<"ai_assistants">;
type AIAssistantInstance = Tables<"ai_assistant_instances">;
type AIOutput = Tables<"ai_outputs">;
type AIAssistantInsert = TablesInsert<"ai_assistants">;
type AIAssistantInstanceInsert = TablesInsert<"ai_assistant_instances">;
type AIOutputInsert = TablesInsert<"ai_outputs">;

// Validation interfaces
interface FormField {
  label: string;
  type: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  description?: string;
}

interface ZodSchema {
  type: string;
  properties: Record<string, unknown>;
}

export const aiAssistantService = {
  // ============ AI ASSISTANTS (System Level) ============

  /**
   * Get all system AI assistants (for public discovery)
   */
  async getSystemAssistants(): Promise<{
    data: AIAssistant[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .select(
          `
          *,
          industries (
            id,
            name,
            emoji
          )
        `,
        )
        .eq("is_active", true)
        .order("category")
        .order("name");

      if (error) {
        console.error("Error fetching system assistants:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch system assistants:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get system assistants filtered by industry
   */
  async getSystemAssistantsByIndustry(industryId?: string): Promise<{
    data: AIAssistant[] | null;
    error: Error | null;
  }> {
    try {
      let query = supabase
        .from("ai_assistants")
        .select("*")
        .eq("is_active", true)
        .order("category")
        .order("name");

      if (industryId) {
        query = query.eq("industry_id", industryId);
      }

      const { data, error } = await query;
      if (error) {
        console.error("Error fetching assistants by industry:", error);
        return { data: null, error: new Error(error.message) };
      }
      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch assistants by industry:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Generate an assistant setup using the Edge Function
   * - Performs fetch with access token
   * - Safely parses JSON
   * - Throws on non-ok or unsuccessful responses
   */
  async generateAssistantSetup(params: {
    description: string;
    organizationId: string;
    accessToken: string;
  }): Promise<{ assistant: AIAssistant; instance: AIAssistantInstance }> {
    const { description, organizationId, accessToken } = params;

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-assistant-setup`;
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ description, organization_id: organizationId }),
      });

      let data: any = null;
      try {
        data = await resp.json();
      } catch (_) {
        data = null;
      }

      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || resp.statusText || "Generation failed");
      }

      const assistant: AIAssistant = data.assistant;
      const instance: AIAssistantInstance = data.instance;
      if (!assistant || !instance) {
        throw new Error("Malformed response from assistant generator");
      }

      return { assistant, instance };
    } catch (error) {
      console.error("Failed to generate assistant setup:", error);
      throw error instanceof Error ? error : new Error("Unknown error");
    }
  },

  /**
   * Create new user-owned assistant (Organization Level)
   * - Non-admin callers should pass their organizationId
   * - Assistants are active by default; visibility defaults to private unless isPublic is true
   */
  async createUserAssistant(
    organizationId: string,
    payload: Omit<
      AIAssistantInsert,
      // These are set server-side in this method
      "id" | "created_at" | "updated_at" | "is_active"
    > & {
      is_public?: boolean;
    },
  ): Promise<{ data: AIAssistant | null; error: Error | null }> {
    try {
      // Ensure required JSON fields have safe defaults
      const safeFormFields = (payload as any).form_fields ?? [];
      const safeZodSchema = (payload as any).zod_schema ?? {
        type: "object",
        properties: { output_content: { type: "string" } },
        required: ["output_content"],
      };

      const insertData: any = {
        ...payload,
        form_fields: safeFormFields,
        zod_schema: safeZodSchema,
        // New ownership/visibility fields added via migration (optional in local types)
        owner_organization_id: organizationId,
        is_public: payload.is_public ?? false,
        // Active by default for non-admins
        is_active: true,
      };

      const { data, error } = await supabase
        .from("ai_assistants")
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error("Error creating user assistant:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to create user assistant:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get all system AI assistants including inactive ones (for admin use)
   */
  async getAllSystemAssistants(): Promise<{
    data: AIAssistant[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .select("*")
        .order("is_active", { ascending: false }) // Show active first
        .order("category")
        .order("name");

      if (error) {
        console.error("Error fetching all system assistants:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch all system assistants:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get system assistants by category
   */
  async getSystemAssistantsByCategory(category: string): Promise<{
    data: AIAssistant[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .select("*")
        .eq("category", category)
        .eq("is_active", true)
        .order("name");

      if (error) {
        console.error("Error fetching assistants by category:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch assistants by category:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get single system assistant by ID
   */
  async getSystemAssistant(assistantId: string): Promise<{
    data: AIAssistant | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .select("*")
        .eq("id", assistantId)
        .eq("is_active", true)
        .single();

      if (error) {
        console.error("Error fetching system assistant:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch system assistant:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Create new system assistant (Super Admin only)
   */
  async createSystemAssistant(assistant: AIAssistantInsert): Promise<{
    data: AIAssistant | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .insert(assistant)
        .select()
        .single();

      if (error) {
        console.error("Error creating system assistant:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to create system assistant:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update system assistant (Super Admin only)
   */
  async updateSystemAssistant(
    assistantId: string,
    updates: TablesUpdate<"ai_assistants">,
  ): Promise<{
    data: AIAssistant | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistants")
        .update(updates)
        .eq("id", assistantId)
        .select()
        .single();

      if (error) {
        console.error("Error updating system assistant:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to update system assistant:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // ============ AI ASSISTANT INSTANCES (Organization Level) ============

  /**
   * Get organization's AI assistant instances
   */
  async getOrganizationInstances(organizationId: string): Promise<{
    data: (AIAssistantInstance & { ai_assistant: AIAssistant })[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .select(
          `
          *,
          ai_assistant:ai_assistants(*)
        `,
        )
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching organization instances:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch organization instances:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get single assistant instance by ID
   */
  async getAssistantInstance(instanceId: string): Promise<{
    data: AIAssistantInstance | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .select("*")
        .eq("id", instanceId)
        .single();

      if (error) {
        console.error("Error fetching assistant instance:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch assistant instance:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Create new assistant instance for organization
   */
  async createAssistantInstance(instance: AIAssistantInstanceInsert): Promise<{
    data: AIAssistantInstance | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .insert(instance)
        .select()
        .single();

      if (error) {
        console.error("Error creating assistant instance:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to create assistant instance:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update assistant instance
   */
  async updateAssistantInstance(
    instanceId: string,
    updates: TablesUpdate<"ai_assistant_instances">,
  ): Promise<{
    data: AIAssistantInstance | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .update(updates)
        .eq("id", instanceId)
        .select()
        .single();

      if (error) {
        console.error("Error updating assistant instance:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to update assistant instance:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update assistant instance model
   */
  async updateAssistantInstanceModel(
    instanceId: string,
    model: string,
  ): Promise<{
    data: AIAssistantInstance | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .update({ model })
        .eq("id", instanceId)
        .select()
        .single();

      if (error) {
        console.error("Error updating assistant instance model:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to update assistant instance model:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // ============ Generator Tool Associations ==========

  /**
   * Get linked generator assistants (tools) for a chat assistant
   */
  async getLinkedToolsForChatAssistant(chatAssistantId: string): Promise<{
    data: Array<{ id: string; title: string; description?: string }>;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("assistant_generators")
        .select(
          `
          generator_assistant_id,
          ai_assistants!assistant_generators_generator_assistant_id_fkey (
            id,
            title,
            description
          )
        `,
        )
        .eq("chat_assistant_id", chatAssistantId);

      if (error) return { data: [], error: new Error(error.message) };

      const mapped = (data || []).map((r: any) => ({
        id: r.ai_assistants.id,
        title: r.ai_assistants.title,
        description: r.ai_assistants.description,
      }));

      return { data: mapped, error: null };
    } catch (e) {
      return {
        data: [],
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  async listGeneratorInstances(organizationId: string): Promise<{
    data: Array<{ id: string; name: string; ai_assistant_id: string }>;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .select("id, name, ai_assistant_id, ai_assistants!inner(supports_chat)")
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .eq("ai_assistants.supports_chat", false);

      if (error) return { data: [], error: new Error(error.message) };

      const mapped = (data || []).map((r: any) => ({
        id: r.id as string,
        name: (r.name as string) || "AI Tool",
        ai_assistant_id: r.ai_assistant_id as string,
      }));
      return { data: mapped, error: null };
    } catch (e) {
      return {
        data: [],
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  async getInstanceGeneratorLinks(
    chatAssistantInstanceId: string,
  ): Promise<{ data: string[]; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("chat_instance_generators")
        .select("generator_assistant_instance_id")
        .eq("chat_assistant_instance_id", chatAssistantInstanceId);

      if (error) return { data: [], error: new Error(error.message) };
      const ids = (data || []).map(
        (r: any) => r.generator_assistant_instance_id as string,
      );
      return { data: ids, error: null };
    } catch (e) {
      return {
        data: [],
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  async addInstanceGeneratorLink(params: {
    organizationId: string;
    chatAssistantId: string;
    chatAssistantInstanceId: string;
    generatorAssistantInstanceId: string;
  }): Promise<{ success: boolean; error: Error | null }> {
    try {
      const {
        organizationId,
        chatAssistantId,
        chatAssistantInstanceId,
        generatorAssistantInstanceId,
      } = params;
      const { error } = await supabase.from("chat_instance_generators").insert({
        organization_id: organizationId,
        chat_assistant_id: chatAssistantId,
        chat_assistant_instance_id: chatAssistantInstanceId,
        generator_assistant_instance_id: generatorAssistantInstanceId,
      } as any);
      if (error) return { success: false, error: new Error(error.message) };
      return { success: true, error: null };
    } catch (e) {
      return {
        success: false,
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  async removeInstanceGeneratorLink(
    chatAssistantInstanceId: string,
    generatorAssistantInstanceId: string,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("chat_instance_generators")
        .delete()
        .eq("chat_assistant_instance_id", chatAssistantInstanceId)
        .eq("generator_assistant_instance_id", generatorAssistantInstanceId);
      if (error) return { success: false, error: new Error(error.message) };
      return { success: true, error: null };
    } catch (e) {
      return {
        success: false,
        error: e instanceof Error ? e : new Error("Unknown error"),
      };
    }
  },

  /**
   * Delete assistant instance
   */
  async deleteAssistantInstance(instanceId: string): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("ai_assistant_instances")
        .delete()
        .eq("id", instanceId);

      if (error) {
        console.error("Error deleting assistant instance:", error);
        return { success: false, error: new Error(error.message) };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error("Failed to delete assistant instance:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // ============ AI OUTPUTS (Generated Content) ============

  /**
   * Create new AI output
   */
  async createAIOutput(output: AIOutputInsert): Promise<{
    data: AIOutput | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .insert(output)
        .select()
        .single();

      if (error) {
        console.error("Error creating AI output:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to create AI output:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get organization's AI outputs with pagination
   */
  async getOrganizationOutputs(
    organizationId: string,
    limit: number = 50,
    offset: number = 0,
    filters?: {
      onlySaved?: boolean;
      onlyExternal?: boolean;
      assistantId?: string;
      searchTerm?: string;
      folderId?: string;
    },
  ): Promise<{
    data:
      | (AIOutput & {
          ai_assistant: AIAssistant;
          ai_assistant_instance?: AIAssistantInstance;
        })[]
      | null;
    error: Error | null;
  }> {
    try {
      let query = supabase
        .from("ai_outputs")
        .select(
          `id, created_at, ai_assistant_id, ai_assistant_instance_id, organization_id, user_id, is_saved, is_public, public_id, input_data, structured_output, output_title, folder_id, is_edit_mode`,
        )
        .eq("organization_id", organizationId);

      // Apply filters
      if (filters?.onlySaved) {
        query = query.eq("is_saved", true);
      }

      if (filters?.onlyExternal) {
        // External outputs are those generated by anonymous/public users (no user_id)
        query = query.is("user_id", null);
      }

      if (filters?.assistantId && filters.assistantId !== "all") {
        query = query.eq("ai_assistant_id", filters.assistantId);
      }

      if (filters?.folderId) {
        if (filters.folderId === "__unassigned__") {
          query = query.is("folder_id", null);
        } else {
          query = query.eq("folder_id", filters.folderId);
        }
      }

      // Add search if provided (basic text search in structured_output and input_data)
      if (filters?.searchTerm) {
        query = query.or(
          `structured_output.ilike.%${filters.searchTerm}%,input_data.ilike.%${filters.searchTerm}%,output_title.ilike.%${filters.searchTerm}%`,
        );
      }

      const { data, error } = await query
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error("Error fetching organization outputs:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch organization outputs:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get user's AI outputs
   */
  async getUserOutputs(
    userId: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<{
    data:
      | (AIOutput & {
          ai_assistant: AIAssistant;
          ai_assistant_instance?: AIAssistantInstance;
        })[]
      | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select(
          `
          *,
          ai_assistant:ai_assistants(*),
          ai_assistant_instance:ai_assistant_instances(*)
        `,
        )
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error("Error fetching user outputs:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch user outputs:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Save an AI output (mark as saved) and optionally assign to a folder
   */
  async saveAIOutput(
    outputId: string,
    folderId?: string,
  ): Promise<{
    data: AIOutput | null;
    error: Error | null;
  }> {
    try {
      const updateData: any = { is_saved: true };
      if (folderId !== undefined) {
        updateData.folder_id = folderId;
      }

      const { data, error } = await supabase
        .from("ai_outputs")
        .update(updateData)
        .eq("id", outputId)
        .select()
        .single();

      if (error) {
        console.error("Error saving AI output:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to save AI output:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Create an AI output from a chat message
   */
  async createChatMessageOutput({
    assistantId,
    assistantInstanceId,
    organizationId,
    userId,
    messageContent,
    messageTitle,
    folderId,
    messageId,
  }: {
    assistantId: string;
    assistantInstanceId: string;
    organizationId: string;
    userId: string;
    messageContent: string;
    messageTitle?: string;
    folderId: string;
    messageId?: string;
  }): Promise<{
    data: AIOutput | null;
    error: Error | null;
  }> {
    try {
      const outputData: any = {
        ai_assistant_id: assistantId,
        ai_assistant_instance_id: assistantInstanceId,
        organization_id: organizationId,
        user_id: userId,
        input_data: { source: "chat_message", message_id: messageId },
        structured_output: messageContent,
        output_title: messageTitle || "Chat Message",
        status: "completed",
        is_saved: true, // Auto-save when creating from chat
        folder_id: folderId,
      };

      const { data, error } = await supabase
        .from("ai_outputs")
        .insert(outputData)
        .select()
        .single();

      if (error) {
        console.error("Error creating chat message output:", error);
        return { data: null, error: new Error(error.message) };
      }

      // Update the chat_messages table to link this message to the output
      if (messageId && data) {
        const { error: updateError } = await supabase
          .from("chat_messages")
          .update({ output_id: data.id })
          .eq("id", messageId);

        if (updateError) {
          console.error(
            "Error updating chat message with output_id:",
            updateError,
          );
          // Don't fail the whole operation, just log the error
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to create chat message output:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async moveOutputToFolder(
    outputId: string,
    folderId: string | null,
  ): Promise<{
    data: AIOutput | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .update({ folder_id: folderId })
        .eq("id", outputId)
        .select()
        .single();

      if (error) {
        console.error("Error moving AI output to folder:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to move AI output to folder:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update AI output content (for inline editing)
   */
  async updateAIOutputContent(
    outputId: string,
    newContent: string | Record<string, any>,
  ): Promise<{
    data: AIOutput | null;
    error: Error | null;
  }> {
    try {
      console.log("🔧 Service: updateAIOutputContent called with:", {
        outputId,
        contentType: typeof newContent,
        contentPreview:
          typeof newContent === "string"
            ? newContent.substring(0, 100)
            : JSON.stringify(newContent).substring(0, 100),
      });

      const { data, error } = await supabase
        .from("ai_outputs")
        .update({
          structured_output: newContent,
          updated_at: new Date().toISOString(),
        })
        .eq("id", outputId)
        .select()
        .single();

      if (error) {
        console.error("❌ Service: Database update error:", error);
        return { data: null, error: new Error(error.message) };
      }

      console.log("✅ Service: Database update successful:", data?.id);
      return { data, error: null };
    } catch (error) {
      console.error(
        "❌ Service: Unexpected error updating AI output content:",
        error,
      );
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update AI output title
   */
  async updateAIOutputTitle(
    outputId: string,
    newTitle: string,
  ): Promise<{ data: AIOutput | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .update({ output_title: newTitle })
        .eq("id", outputId)
        .select()
        .single();

      if (error) {
        console.error("Error updating AI output title:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to update AI output title:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Set persistent edit mode flag for an output
   */
  async setOutputEditMode(
    outputId: string,
    isEditMode: boolean,
  ): Promise<{ data: AIOutput | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .update({ is_edit_mode: isEditMode } as any)
        .eq("id", outputId)
        .select()
        .single();

      if (error) {
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get AI output by ID
   */
  async getOutputById(outputId: string): Promise<{
    data: AIOutput | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select("*")
        .eq("id", outputId)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          // No rows returned
          return { data: null, error: null };
        }
        console.error("Error fetching AI output by ID:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch AI output by ID:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get saved AI outputs for an assistant instance
   */
  async getSavedOutputs(instanceId: string): Promise<{
    data: AIOutput[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select("*")
        .eq("ai_assistant_instance_id", instanceId)
        .eq("is_saved", true)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching saved outputs:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch saved outputs:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get all AI outputs (internal and external) for an assistant instance
   */
  async getAllOutputs(instanceId: string): Promise<{
    data: AIOutput[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select("*")
        .eq("ai_assistant_instance_id", instanceId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching all outputs:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch all outputs:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get external (public) AI outputs for an assistant instance
   */
  async getExternalOutputs(instanceId: string): Promise<{
    data: AIOutput[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select("*")
        .eq("ai_assistant_instance_id", instanceId)
        .is("user_id", null)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching external outputs:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data, error: null };
    } catch (error) {
      console.error("Failed to fetch external outputs:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Delete a saved AI output
   */
  async deleteSavedOutput(outputId: string): Promise<{
    data: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("ai_outputs")
        .update({ is_saved: false })
        .eq("id", outputId);

      if (error) {
        console.error("Error unsaving AI output:", error);
        return { data: false, error: new Error(error.message) };
      }

      return { data: true, error: null };
    } catch (error) {
      console.error("Failed to unsave AI output:", error);
      return {
        data: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Permanently delete an AI output
   */
  async deleteAIOutput(outputId: string): Promise<{
    data: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("ai_outputs")
        .delete()
        .eq("id", outputId);

      if (error) {
        console.error("Error deleting AI output:", error);
        return { data: false, error: new Error(error.message) };
      }

      return { data: true, error: null };
    } catch (error) {
      console.error("Failed to delete AI output:", error);
      return {
        data: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Find or create an assistant instance for organization
   * If instance exists, return it. If not, create a new one.
   */
  async findOrCreateAssistantInstance(
    assistantId: string,
    organizationId: string,
    instanceName?: string,
  ): Promise<{
    data: AIAssistantInstance | null;
    error: Error | null;
    created: boolean;
  }> {
    try {
      // First, try to find existing instance
      const { data: existingInstances, error: fetchError } = await supabase
        .from("ai_assistant_instances")
        .select("*")
        .eq("ai_assistant_id", assistantId)
        .eq("organization_id", organizationId)
        .eq("is_active", true)
        .limit(1);

      if (fetchError) {
        console.error("Error fetching existing instance:", fetchError);
        return {
          data: null,
          error: new Error(fetchError.message),
          created: false,
        };
      }

      // If instance exists, return it
      if (existingInstances && existingInstances.length > 0) {
        return { data: existingInstances[0], error: null, created: false };
      }

      // If no instance exists, create one
      const { data: assistant, error: assistantError } = await supabase
        .from("ai_assistants")
        .select("*")
        .eq("id", assistantId)
        .single();

      if (assistantError || !assistant) {
        console.error("Error fetching assistant:", assistantError);
        return {
          data: null,
          error: new Error("Assistant not found"),
          created: false,
        };
      }

      // Create new instance with base assistant data
      const instanceData: AIAssistantInstanceInsert = {
        ai_assistant_id: assistantId,
        organization_id: organizationId,
        name: instanceName || assistant.name,
        title: null, // Don't copy - let it be null until user customizes
        description: null, // Don't copy - let it be null until user customizes
        icon_url: null, // Don't copy - let it be null until user customizes
        cta_label: null, // Don't copy - let it be null until user customizes
        is_active: true,
      };

      const { data: newInstance, error: createError } = await supabase
        .from("ai_assistant_instances")
        .insert(instanceData)
        .select()
        .single();

      if (createError) {
        console.error("Error creating assistant instance:", createError);
        return {
          data: null,
          error: new Error(createError.message),
          created: false,
        };
      }

      // No per-instance seeding anymore; tools resolve from base-level links

      return { data: newInstance, error: null, created: true };
    } catch (error) {
      console.error("Failed to find or create assistant instance:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
        created: false,
      };
    }
  },

  // ============ UTILITY METHODS ============

  /**
   * Get AI assistant categories
   */
  getCategories(): string[] {
    return [
      "Sales",
      "Marketing",
      "Daily work",
      "Customer Engagement",
      "Operations",
      "Legal",
      "Finance",
      "HR",
    ];
  },

  /**
   * Validate form fields structure
   */
  validateFormFields(formFields: unknown): formFields is FormField[] {
    if (!Array.isArray(formFields)) return false;

    return formFields.every(
      (field) =>
        field &&
        typeof field === "object" &&
        "label" in field &&
        "type" in field &&
        "required" in field &&
        typeof field.label === "string" &&
        typeof field.type === "string" &&
        typeof field.required === "boolean",
    );
  },

  /**
   * Validate Zod schema structure
   */
  validateZodSchema(zodSchema: unknown): zodSchema is ZodSchema {
    if (!zodSchema || typeof zodSchema !== "object") return false;

    const schema = zodSchema as Record<string, unknown>;

    return (
      schema.type === "object" &&
      schema.properties &&
      typeof schema.properties === "object"
    );
  },

  // ============ PUBLIC SHARING ============

  /**
   * Make an AI output public and generate a shareable link
   */
  async makeOutputPublic(outputId: string): Promise<{
    data: { publicId: string } | null;
    error: Error | null;
  }> {
    try {
      // First, generate a unique public ID using the database function
      const { data: publicIdData, error: publicIdError } = await supabase.rpc(
        "generate_ai_output_public_id",
      );

      if (publicIdError) {
        console.error("Error generating public ID:", publicIdError);
        return { data: null, error: new Error(publicIdError.message) };
      }

      const publicId = publicIdData as string;

      // Update the output to make it public
      const { data, error } = await supabase
        .from("ai_outputs")
        .update({
          is_public: true,
          public_id: publicId,
        })
        .eq("id", outputId)
        .select("public_id")
        .single();

      if (error) {
        console.error("Error making output public:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data: { publicId: data.public_id }, error: null };
    } catch (error) {
      console.error("Failed to make output public:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Make an AI output private (remove public access)
   */
  async makeOutputPrivate(outputId: string): Promise<{
    data: boolean | null;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("ai_outputs")
        .update({
          is_public: false,
          public_id: null,
        })
        .eq("id", outputId);

      if (error) {
        console.error("Error making output private:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data: true, error: null };
    } catch (error) {
      console.error("Failed to make output private:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get public AI output by public ID (for public viewing)
   */
  async getPublicOutput(publicId: string): Promise<{
    data:
      | (AIOutput & {
          ai_assistant: AIAssistant;
          ai_assistant_instance?: AIAssistantInstance;
          organization: {
            logo_url?: string;
            brand_color?: string;
          };
        })
      | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select(
          `
          *,
          ai_assistant:ai_assistants(*),
          ai_assistant_instance:ai_assistant_instances(*),
          organization:organizations(name, logo_url, brand_color)
        `,
        )
        .eq("public_id", publicId)
        .eq("is_public", true)
        .single();

      if (error) {
        console.error("Error fetching public output:", error);
        return { data: null, error: new Error(error.message) };
      }

      return { data: data as any, error: null };
    } catch (error) {
      console.error("Failed to fetch public output:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Check if an output is public
   */
  async isOutputPublic(outputId: string): Promise<{
    data: { isPublic: boolean; publicId?: string } | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_outputs")
        .select("is_public, public_id")
        .eq("id", outputId)
        .single();

      if (error) {
        console.error("Error checking output public status:", error);
        return { data: null, error: new Error(error.message) };
      }

      return {
        data: {
          isPublic: data.is_public || false,
          publicId: data.public_id || undefined,
        },
        error: null,
      };
    } catch (error) {
      console.error("Failed to check output public status:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get assistant instances for an organization (for displaying customized cards)
   */
  async getOrganizationAssistantInstances(organizationId: string): Promise<{
    data: (AIAssistantInstance & { ai_assistant: AIAssistant })[] | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .select(
          `
          *,
          ai_assistant:ai_assistants(*)
        `,
        )
        .eq("organization_id", organizationId)
        .eq("ai_assistant.is_active", true)
        .not("ai_assistant", "is", null) // Explicitly filter out null ai_assistant relationships
        .order("created_at", { ascending: false });

      if (error) {
        console.error(
          "Error fetching organization assistant instances:",
          error,
        );
        return { data: null, error: new Error(error.message) };
      }

      // Additional client-side filtering to ensure data integrity
      const filteredData =
        data?.filter(
          (
            instance,
          ): instance is AIAssistantInstance & { ai_assistant: AIAssistant } =>
            instance.ai_assistant !== null &&
            instance.ai_assistant !== undefined,
        ) || [];

      return {
        data: filteredData,
        error: null,
      };
    } catch (error) {
      console.error("Failed to fetch organization assistant instances:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  // ============ INSTANCE PUBLIC SHARING ============

  async makeInstancePublic(instanceId: string): Promise<{
    data: { publicId: string } | null;
    error: Error | null;
  }> {
    try {
      const { data: publicIdData, error: publicIdError } = await supabase.rpc(
        "generate_ai_output_public_id",
      );
      if (publicIdError) {
        return { data: null, error: new Error(publicIdError.message) };
      }
      const publicId = publicIdData as string;

      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .update({ is_public: true, public_id: publicId } as any)
        .eq("id", instanceId)
        .select("public_id")
        .single();

      if (error) return { data: null, error: new Error(error.message) };
      return { data: { publicId: data.public_id }, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async makeInstancePrivate(instanceId: string): Promise<{
    data: boolean | null;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("ai_assistant_instances")
        .update({ is_public: false, public_id: null } as any)
        .eq("id", instanceId);
      if (error) return { data: null, error: new Error(error.message) };
      return { data: true, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  async isInstancePublic(instanceId: string): Promise<{
    data: { isPublic: boolean; publicId?: string } | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("ai_assistant_instances")
        .select("is_public, public_id")
        .eq("id", instanceId)
        .single();
      if (error) return { data: null, error: new Error(error.message) };
      return {
        data: {
          isPublic: !!data.is_public,
          publicId: data.public_id || undefined,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },
};
